import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { FEATURED_DIVISIONS, HIGHLIGHTS } from "../data/divisions";
import { Reveal } from "../components/Reveal";

export default function Home() {
  return (
    <div className="pt-16 md:pt-20" data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="hero-gradient" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-28 pb-24 md:pb-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22,1,0.36,1] }}
            className="max-w-4xl"
          >
            <div className="text-xs md:text-sm uppercase tracking-[0.3em] text-forest font-semibold mb-6" data-testid="hero-eyebrow">
              A diversified business group
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-[92px] font-extrabold text-navy tracking-tighter leading-[0.95]">
              One Group.<br />
              Every Industry.<br />
              <span className="text-forest">Built on Trust.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate max-w-2xl leading-relaxed">
              Stone Shire Group operates across construction, international trade, and technology — and welcomes partnerships across any sector.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/partner" className="btn-primary rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2" data-testid="hero-cta-partner">
                Partner with us <ArrowRight size={16} />
              </Link>
              <Link to="/divisions" className="rounded-full px-7 py-3.5 text-sm font-semibold border border-navy/15 text-navy hover:border-navy/40 transition-colors inline-flex items-center gap-2" data-testid="hero-cta-divisions">
                Explore divisions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* GROUP INTRO */}
      <section className="bg-stone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] text-forest font-semibold">The Group</div>
          </Reveal>
          <Reveal className="md:col-span-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy tracking-tight leading-tight">
              A multi-division holding built for reliability across sectors — construction, trade, and technology today, more to come as the group grows.
            </h2>
            <p className="mt-6 text-slate text-lg leading-relaxed max-w-3xl">
              [Add content here] — a short paragraph describing the group&apos;s origin, principles, and the value of diversification.
            </p>
          </Reveal>
        </div>
      </section>

      {/* DIVISIONS PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-forest font-semibold mb-3">Our Divisions</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy tracking-tight">Six flagship disciplines. Twenty-four more on the shelf.</h2>
            </div>
            <Link to="/divisions" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-forest transition-colors" data-testid="home-divisions-link">
              View all services <ArrowUpRight size={16}/>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURED_DIVISIONS.slice(0, 6).map((d, i) => (
              <Reveal key={d.id} delay={i}>
                <Link to={`/divisions#${d.id}`} data-testid={`home-division-card-${d.id}`} className="block group card-lift rounded-2xl overflow-hidden border border-stone bg-white h-full">
                  {d.image ? (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={d.image} alt={d.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-stone flex items-center justify-center">
                      <d.icon size={56} className="text-forest" strokeWidth={1.4}/>
                    </div>
                  )}
                  <div className="p-7">
                    <div className="flex items-center gap-3 text-forest mb-3">
                      <d.icon size={18} />
                      <span className="text-xs uppercase tracking-widest font-semibold">{d.category}</span>
                    </div>
                    <div className="text-xl font-bold text-navy tracking-tight">{d.name}</div>
                    <div className="mt-2 text-slate text-sm leading-relaxed">{d.tagline}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link
              to="/divisions#directory"
              data-testid="home-view-all-services"
              className="inline-flex items-center gap-2 rounded-full border border-navy/20 text-navy px-7 py-3.5 text-sm font-semibold hover:border-navy/60 transition-colors"
            >
              View all 30+ services <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS BAR */}
      <section className="bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {HIGHLIGHTS.map((h) => (
            <div key={h.label} data-testid={`highlight-${h.label}`}>
              <div className="text-4xl md:text-5xl font-extrabold tracking-tight" style={{color:"#8AA795"}}>{h.number}</div>
              <div className="mt-2 text-white/70 text-sm leading-relaxed max-w-[220px]">{h.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS — hidden until real client feedback is available.
          To re-enable: restore the <section> block from git history and re-import TESTIMONIALS. */}

      {/* PARTNER CTA */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="rounded-3xl bg-navy text-white p-10 md:p-20 relative overflow-hidden grain">
            <div className="max-w-3xl relative">
              <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
                Open to bold partnerships<br /> across every industry.
              </h2>
              <p className="mt-6 text-white/70 text-lg max-w-xl">
                Whether you need a service, want to partner, or work with us as a vendor — start the conversation.
              </p>
              <Link to="/partner" className="mt-10 inline-flex items-center gap-2 rounded-full bg-white text-navy px-7 py-3.5 text-sm font-semibold hover:bg-white/90 transition-colors" data-testid="cta-partner-final">
                Partner with us <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
